import {
  bigqueryDefaults,
  setDiagnosticsOptions,
} from "monaco-bq/release/esm/monaco.contribution";
import "monaco-bq/release/esm/monaco-bq.css";
import type {
  SchemaColumn,
  SchemaProject,
} from "monaco-bq/release/esm/monaco.contribution";
import type { Dataset, Field } from "../store";

bigqueryDefaults.workerPath = "/monaco-bq-1.1.0/bigquery.worker.js";

const defaultSettings = {
  bqlsPath: "/monaco-bq-1.1.0/bqls.wasm",
  parser: {
    mode: "script" as const,
    qualifyReserved: true,
    reserveGraphTable: false,
    reserveMatchRecognize: false,
    supportedGenericEntityTypes: [],
    supportedGenericSubEntityTypes: [],
    languageFeatures: "MAXIMUM",
    macroExpansionMode: "none" as const,
  },
  formatter: {
    printWidth: 80,
    keywordCase: "upper" as const,
  },
};

let projects: SchemaProject[] = [];
let keywordCase: "lower" | "upper" = "upper";

function updateDiagnosticsOptions() {
  setDiagnosticsOptions({
    ...bigqueryDefaults.diagnosticsOptions,
    ...defaultSettings,
    formatter: {
      ...defaultSettings.formatter,
      keywordCase,
    },
    projects,
  });
}

updateDiagnosticsOptions();

export function configureBigqueryLanguageProvider(
  projectId: string,
  datasets: Dataset[],
) {
  projects = [
    {
      project_id: projectId,
      default: true,
      datasets: datasets.map((dataset) => ({
        dataset_id: dataset.name,
        default: dataset.default,
        tables: (dataset.tables ?? []).map((table) => ({
          name: table.name,
          description: table.description,
          columns: table.fields.map(fieldToSchemaColumn),
        })),
        functions: (dataset.funcs ?? []).map((func) => ({
          name: func.name,
          description: func.description,
          arguments: func.signature?.args,
          return_type: func.signature?.ret,
        })),
      })),
    },
  ];

  updateDiagnosticsOptions();
}

export function configureBigqueryKeywordCase(lowerCase: boolean) {
  keywordCase = lowerCase ? "lower" : "upper";
  updateDiagnosticsOptions();
}

export function registerBigqueryInlineCompletionProvider(
  monacoModule: typeof import("monaco-editor"),
  inlineCompletion: (
    model: import("monaco-editor").editor.ITextModel,
    text: string,
  ) => Promise<string>,
) {
  const pendingCompletions = new Map<string, Promise<string>>();

  return monacoModule.languages.registerInlineCompletionsProvider("bigquery", {
    provideInlineCompletions: async (model, position, _context, token) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (token.isCancellationRequested) {
        return { items: [] };
      }

      const beforeText = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });
      const beforeTextLine = model
        .getLineContent(position.lineNumber)
        .slice(0, position.column - 1);
      const afterText = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: model.getLineCount(),
        endColumn: model.getLineLength(model.getLineCount()) + 1,
      });
      const query = `${beforeText}<<CURSOR>>${afterText}`;
      let completionPromise = pendingCompletions.get(query);
      if (!completionPromise) {
        completionPromise = inlineCompletion(model, query);
        pendingCompletions.set(query, completionPromise);
      }
      let completion: string;
      try {
        completion = await completionPromise;
      } finally {
        if (pendingCompletions.get(query) === completionPromise) {
          pendingCompletions.delete(query);
        }
      }
      if (!completion || token.isCancellationRequested) {
        return { items: [] };
      }

      return {
        items: [
          {
            insertText: `${beforeTextLine}${completion}`,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: 1,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
          },
        ],
      };
    },
    disposeInlineCompletions: () => {},
  });
}

function fieldToSchemaColumn(field: Field): SchemaColumn {
  return {
    name: field.name,
    type: field.type,
    description: field.description,
    fields: field.fields?.map(fieldToSchemaColumn),
  };
}
